import { NgClass, NgIf } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, computed, effect, forwardRef,
  input,
  output,
  viewChild
} from "@angular/core";
import { Router } from "@angular/router";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { CheckedState, TreeItem, TreeItemLookup, TreeViewComponent, TreeViewModule } from "@progress/kendo-angular-treeview";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { ControlResizerOptions, ControlResizerService } from "@mysrc/services/framework";
import {
  AuthPermIdentifier, RequestDelete, RequestUserPermission, RequestUserPermissionSet,
  TreeDataItem, TreeDataItemCheckHighlighted, TreeInfoAuth
} from "@myapp/modules/fwcore/models";
import {
  AuthorizationService, ConfirmationDialogService, NotificationDialogService,
  NotificationPopupService
} from "@myapp/modules/fwcore/services";
import { AuthPerm } from "@myapp/features/admin/models";
import {
  RequestPermission, RequestPermissionSet, RequestPermissionTree,
  RequestRolePerm, RequestRolePermSet
} from "@myapp/features/admin/models/messages";
import { AdminService } from '@myapp/features/admin/services';
import { AuthTreeEditorComponent } from "../..";
import { IsgSizerDirective } from "../../../../prob/directives/isg-sizer.directive";

@Component({
  selector: 'app-auth-tree',
  templateUrl: './auth-tree.component.html',
  styleUrls: ['./auth-tree.component.scss'],
  standalone: true,
  imports: [ButtonModule, IsgSizerDirective, NgIf, TreeViewModule, NgClass
    , forwardRef(() => AuthTreeEditorComponent)]
})
export class AuthTreeComponent implements AfterViewInit, OnDestroy {

  //----------------------------  Constants  ---------------------------

  /** Direction value for moving an item up when reordering. */
  protected readonly ctDirectionUp = 'up';

  /** Dialog message shown when a save fails. */
  protected readonly ctMsgError = 'Something went wrong';

  /** Dialog message shown when permissions save successfully. */
  protected readonly ctMsgPermsSaved = 'Permissions saved successfully';

  /** Source value identifying user-sourced tree items. */
  protected readonly ctSourceUser = 'User';

  /** Dialog title for failed operations. */
  protected readonly ctTitleError = 'Error';

  /** Dialog title for successful operations. */
  protected readonly ctTitleSuccess = 'Success';

  //--------------------  Input / Output Properties  -------------------

  /** Module ids used to filter the permission tree fetch. */
  public moduleIds = input<string[] | undefined | null>(null);

  /** License id context for the tree. */
  public licenseId = input<string | null>(null);

  /** Hierarchy id context for the tree. */
  public hierarchyId = input<string | null>(null);

  /** Role id whose permissions are loaded/saved. */
  public roleId = input<string | null | undefined>(null);

  /** User id whose permissions are loaded/saved. */
  public userFilterId = input<string | null | undefined>(null);

  /** Auth types to include in the fetch. */
  public authTypes = input<string[] | null>(null);

  /** Auth types to exclude from the fetch. */
  public authTypesExclude = input<string[] | null>(null);

  /** Filters items by active state. */
  public active = input<boolean | null>(null);

  /** Cascades link behaviour when true. */
  public cascadeLink = input<boolean | null>(null);

  /** Includes member nodes in the fetch when true. */
  public includeMembers = input<boolean | null>(null);

  /** Includes hierarchy nodes in the fetch when true. */
  public includeHierarchies = input<boolean | null>(null);

  /** Includes label nodes in the fetch when true. */
  public includeLabels = input<boolean | null>(null);

  /** Process filter applied to the fetch. */
  public processFilter = input<number | null>(null);

  /** Triggers a save of role or user permissions when set true. */
  public triggerSave = input<boolean | null>(null);

  /** Shows the toolbar when true. */
  public showToolbar = input(false);

  /** Shows the save button when true. */
  public showSaveButton = input(false);

  /** Enables the save button when true. */
  public enableSaveButton = input(false);

  /** Shows the create button when true. */
  public showCreateButton = input(false);

  /** Shows the edit button when true. */
  public showEditButton = input(false);

  /** Shows the read-only button when true. */
  public showReadOnlyButton = input(false);

  /** Shows the reorder buttons when true. */
  public showReorderButton = input(false);

  /** Shows the delete button when true. */
  public showDeleteButton = input(false);

  /** Retained for legacy callers; sizing is now pane-driven */
  public minHeight = input(200);

  /** Retained for legacy callers; sizing is now pane-driven */
  public widthThreshold = input(600);

  /** Total height the component may occupy */
  public heightControl = input(300);

  /** Ids of items to mark as checked; applied to the loaded items. */
  public checkedIds = input<string[]>([]);

  /** Ids of items to mark as highlighted; applied to the loaded items. */
  public highlightedIds = input<string[]>([]);

  /** Allows the user to change checkbox state; when false checks are display-only. */
  public allowUserChecks = input(true);

  /** Emits when a permissions save completes. */
  public permissionsSavedChange = output<boolean>();

  /** Emits the selected tree item, or null when selection clears. */
  public selected = output<TreeDataItem | null>();

  /** Emits the number of items loaded. */
  public dataLoaded = output<number | null>();

  /** True when user checkbox changes are locked. */
  protected checksLocked = computed(() => this.showSaveButton() !== true || this.allowUserChecks() !== true);

  //----------------------  ViewChild Properties  ----------------------

  /** Reference to the kendo treeview. */
  protected treeViewComp = viewChild(TreeViewComponent);

  //------------------------  Public Properties  -----------------------

  /** Currently selected tree item. */
  public selectedItem!: TreeDataItem | null;

  /** Keys of the selected tree items. */
  public selectedKeys: string[] = [];

  /** Currently checked tree items. */
  public checkedItems: TreeDataItem[] = [];

  /** Keys of the checked tree items. */
  public checkedKeys: string[] = [];

  /** Permission item loaded into the editor dialog. */
  public editDataItem!: AuthPerm | null;

  /** Property used as the tree item key. */
  public key = "id";

  /** Text values of all parent nodes. */
  public allParentNodes: string[] = [];

  /** Keys of the currently expanded nodes. */
  public expandedKeys: string[] = this.allParentNodes.slice();

  /** Informational message for display. */
  public messageInfo: string | null = null;

  //------------------------  State Properties  ------------------------

  /** Module key applied to the last tree fetch */
  private currentModuleKey: string | null = null;

  /** Role id applied to the last role-permission fetch */
  private currentRoleIdTracked: string | null | undefined = null;

  /** True once the role effect has processed its first value. */
  private roleInitialized: boolean = false;

  /** Emits on component destruction to complete open subscriptions. */
  protected destroy$ = new Subject();

  /** True when the editor is opened in view-only mode. */
  protected isViewOnlyEditor: boolean = false;

  /** True while data is loading. */
  protected loading: boolean = true;

  //-------------------  Data Collection Properties  -------------------

  /** Checked permission identifiers pending save. */
  protected checkedDataItems: AuthPermIdentifier[] = [];

  /** Copy of the checked keys used to revert disallowed changes. */
  protected checkedKeysCopy: string[] = [];

  /** Ids of user-sourced custom items. */
  protected customIds: string[] = [];

  /** Tree items displayed in the treeview. */
  protected dataItems: TreeDataItemCheckHighlighted[] = [];

  /** Ids of excluded items. */
  protected excludedIds: string[] = [];

  /** Ids of items containing custom children. */
  protected hasChildCustomIds: string[] = [];

  /** Ids of items containing excluded children. */
  protected hasChildExcludedIds: string[] = [];

  /** Tree auth info applied via setCheckedSelection. */
  protected treeInfos: TreeInfoAuth[] = [];

  //----------------------  Permission Properties  ---------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {
  }

  //---------------------------  Constructor  --------------------------

  constructor(
    protected authService: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected notification: NotificationPopupService
  ) {

    this.setupEffectHandlers();
  }

  //-------------------------  Public Methods  -------------------------

  /** @deprecated Sizing is measured internally; retained so legacy callers compile. */
  public heightChange(_options?: ControlResizerOptions): void {
  }

  /** Applies checked/excluded/custom state from the supplied tree auth info. */
  public setCheckedSelection(items: TreeInfoAuth[]) {

    this.treeInfos = items;

    let ids: string[] = [];
    let excludedIds: string[] = [];
    let hasChildExcludedIds: string[] = [];
    let customIds: string[] = [];
    let hasChildCustomIds: string[] = [];

    if (items
      && items.length > 0) {

      ids = items.filter(item => !item.excluded).map(item => item.id!);
      excludedIds = items.filter(item => item.excluded).map(item => item.id!);
      hasChildExcludedIds = items.filter(item => (item.hasChildExcludedIds === true)).map(item => item.id!);
      customIds = items.filter(item => item.source == this.ctSourceUser && item.excluded !== true).map(item => item.id!);
      hasChildCustomIds = items.filter(item => (item.hasChildCustomIds === true)).map(item => item.id!);

    }

    this.applyCheckedIds(ids);
    this.excludedIds = excludedIds;
    this.hasChildExcludedIds = hasChildExcludedIds;
    this.customIds = customIds;
    this.hasChildCustomIds = hasChildCustomIds;

    // refresh tree
    this.checkedKeysCopy = ids;
    this.checkedKeys = [...this.checkedKeysCopy];
  }

  /** Returns the ids of the currently checked items. */
  public getCheckedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  /** Returns the ids of the currently highlighted items. */
  public getHighlightedIds(): string[] {

    const highlightedIds: string[] = this.dataItems
      .filter((item) => item.highlighted)
      .map((item) => item.id!);

    return highlightedIds ?? [];
  }

  /** Applies the supplied ids as the checked items. */
  public setCheckedIds(ids: string[]): void {

    this.applyCheckedIds(ids);
  }

  /** Applies the supplied ids as the highlighted items. */
  public setHighlightedIds(ids: string[]): void {

    this.applyHighlightedIds(ids);
  }

  /** Resolves the checked state (checked/indeterminate/none) for a tree item. */
  public isChecked = (dataItem: TreeDataItem, index: string): CheckedState => {

    // Check that there are no children
    if (!Array.isArray(dataItem.items)
      || !dataItem.items.length) {

      if (this.containsCheckedItem(dataItem))
        return "checked";
      else
        return "none";

    }
    else {

      // has children
      let v = this.hasCheckedChildren(dataItem.items);

      if (v === 1) {
        return "checked";
      }
      else if (v === -1) {
        return "indeterminate";
      }
      else
        return "none";

    }

    return "none";
  }

  //------------------------  Lifecycle Methods  -----------------------

  /**
   * Captures the current route for permission requests
   */
  ngAfterViewInit(): void {

    this.reqPerm.route = this.router.url;
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reacts to input changes; replaces the former ngOnChanges wiring. */
  protected setupEffectHandlers(): void {

    effect(() => {

      const key = this.moduleIds()?.filter(x => !!x).join() ?? '';

      if (key === this.currentModuleKey) {
        return;
      }

      this.currentModuleKey = key;

      if (key.length === 0) {
        return;
      }

      this.getPermissionTrees();
    });

    effect(() => {

      const roleId = this.roleId();

      if (!this.roleInitialized) {

        this.roleInitialized = true;
        this.currentRoleIdTracked = roleId;

        if (roleId) {
          this.getRolePermission();
        }

        return;

      }

      if (roleId !== this.currentRoleIdTracked) {
        this.currentRoleIdTracked = roleId;
        this.getRolePermission();
      }
    });

    effect(() => {

      if (this.triggerSave() !== true) {
        return;
      }

      const roleId = this.roleId();
      const userFilterId = this.userFilterId();

      if (roleId
        && roleId.length > 0) {
        this.saveRolePermissions();
      }
      else if (userFilterId
        && userFilterId.length > 0) {
        this.saveUserPermissions();
      }
    });

    // Applies parent-supplied checked ids to the currently loaded items;
    // replaces the former checkedIds input setter.
    effect(() => {

      this.applyCheckedIds(this.checkedIds());
    });

    // Applies parent-supplied highlighted ids to the currently loaded items;
    // replaces the former highlightedIds input setter.
    effect(() => {

      this.applyHighlightedIds(this.highlightedIds());
    });
  }

  //-------------------------  Event Handlers  -------------------------

  /** Opens the editor in add mode. */
  protected onAddClick() {

    this.isViewOnlyEditor = false;
    this.getPermissionDetail(true); //sets true for add to get heraricyID from API
  }

  /** Opens the editor in edit mode for the selected item. */
  protected onEditClick() {

    this.isViewOnlyEditor = false;
    this.getPermissionDetail();
  }

  /** Confirms and deletes the selected permission. */
  protected onDeleteClick() {

    this.dialogConfirm.open('Confirm Deletion', 'Are you sure you want to delete this permission?')
      .subscribe((result: any) => {

        if (result.value
          && this.selectedItem?.id) {
          this.deletePermission(this.selectedItem.id);
        }
      });
  }

  /** Opens the editor in view-only mode for the selected item. */
  protected onViewClick() {

    this.isViewOnlyEditor = true;
    this.getPermissionDetail();
  }

  /** Toggles expansion of the selected node (or all nodes). */
  protected onToggle() {

    this.ToggleNodes(this.selectedItem);
  }

  /** Saves the user or role permissions depending on context. */
  protected onSaveClick() {

    const userFilterId = this.userFilterId();
    const roleId = this.roleId();

    if (userFilterId
      && userFilterId.length > 0) {
      this.saveUserPermissions();
    }
    else if (roleId
      && roleId.length > 0) {
      this.saveRolePermissions();
    }
  }

  /** Moves the selected item up or down within its siblings. */
  protected onMoveItem(direction: 'up' | 'down'): void {

    if (!this.selectedItem
      || !this.selectedItem.id) {
      return; // No selected item, do nothing
    }

    const itemId = this.selectedItem.id;

    this.dataItems = this.moveItemWithinSiblings(this.dataItems, itemId, direction);
  }

  /** Closes the editor without saving. */
  protected onCancelEditor(): void {

    this.editDataItem = null;
  }

  /** Persists the editor changes as a create or update. */
  protected onSaveEditor(permission: AuthPerm): void {

    const request: RequestPermissionSet = {
      data: [permission]
    };

    if (permission.id) {
      this.updatePermission(request);
    } else {
      this.createPermission(request);
    }

    this.resetSelectedItems();
  }

  /** Handles tree selection changes, toggling off a reselected item. */
  protected onSelectionChange(event: TreeItem): void {

    if (this.selectedItem?.id === event.dataItem?.id) {

      this.selectedItem = null;
      this.selectedKeys = [];

      document.activeElement?.querySelector('.k-focus')?.classList.remove('k-focus');

    }
    else {
      this.selectedItem = event.dataItem;
    }
  }

  /** Reverts check changes when checking is locked. */
  protected onItemChecking(itemLookup: TreeItemLookup): void {

    if (this.checksLocked()) {
      queueMicrotask(() => {
        this.checkedKeys = [...this.checkedKeysCopy];
      });
    }
  }

  //---------------------  Tree Expansion Methods  ---------------------

  /** Toggles expansion for the supplied item, or all nodes when null. */
  protected ToggleNodes(selectedItem: TreeDataItem | null) {

    if (!selectedItem) {
      this.expandedKeys = this.expandedKeys.length === 0 ? this.allParentNodes.slice() : [];
      return;
    }

    if (this.isItemSelected(selectedItem)) {
      this.collapseChildren(selectedItem);
    } else {
      this.expandChildren(selectedItem);
    }
  }

  /** Recursively expands the item and its children. */
  protected expandChildren(item: TreeDataItem) {

    if (item.items
      && item.text) {
      this.expandedKeys = [...this.expandedKeys, item.text];
      item.items.forEach(child => this.expandChildren(child));
    }
  }

  /** Recursively collapses the item and its children. */
  protected collapseChildren(item: TreeDataItem) {

    if (item.items) {
      this.expandedKeys = this.expandedKeys.filter(key => key !== item.text);
      item.items.forEach(child => this.collapseChildren(child));
    }
  }

  /** True when the item is currently expanded. */
  protected isItemSelected(item: TreeDataItem): boolean {

    if (item.text) {
      return this.expandedKeys.includes(item.text);
    }

    return false;
  }

  /** Collects the text of all parent nodes into allParentNodes. */
  protected getAllParentTextProperties(items: TreeDataItem[]) {

    items.forEach(i => {

      if (i.text
        && i.items) {
        this.allParentNodes.push(i.text);
        this.getAllParentTextProperties(i.items);
      }
    });
  }

  //----------------------  Tree Reorder Methods  ----------------------

  /** Moves an item up or down within its sibling collection. */
  protected moveItemWithinSiblings(data: TreeDataItem[], itemId: string, direction: 'up' | 'down'): TreeDataItem[] {

    const index = this.findIndexInTree(data, itemId);

    if (index === -1) {
      return data; // Item not found, return unchanged data
    }

    const itemToMove = this.findItemInTree(data, itemId);

    if (!itemToMove) {
      return data; // Item not found, return unchanged data
    }

    // Find the parent of the item
    const parent = this.findParent(data, itemId);

    if (!parent
      || !parent.items) {
      return data; // Parent not found or has no items
    }

    const siblings = parent.items;

    if (siblings.length <= 1) {
      return data; // No siblings or only one sibling, no change needed
    }

    const targetIndex = direction === this.ctDirectionUp ? index - 1 : index + 1;

    if (targetIndex >= 0
      && targetIndex < siblings.length) {
      this.moveItemAmongSiblings(siblings, index, targetIndex, direction);
    }

    return data;
  }

  /** Persists the reorder to the server, then reinserts the moved item. */
  protected moveItemAmongSiblings(siblings: TreeDataItem[], index: number, newIndex: number, direction: 'up' | 'down'): void {

    if (newIndex < 0
      || newIndex >= siblings.length) {
      return; // Invalid newIndex
    }

    this.loading = true;

    const [movedItem] = siblings.splice(index, 1);

    const itemReorder: AuthPerm = { id: movedItem.id };

    // update server
    const request: RequestPermissionSet = {
      data: [itemReorder]
      , processFilter: (direction === this.ctDirectionUp) ? 1 : 2
    };

    this.authService.permissionUpdate$(request)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {
          this.loading = false;
          siblings.splice(newIndex, 0, movedItem);
          this.updateSelectedItemAndKeys(movedItem);
        }))
      .subscribe(
        (response) => {
        }
      );
  }

  /** Sets the selection to the supplied item. */
  protected updateSelectedItemAndKeys(item: TreeDataItem): void {

    this.selectedItem = item;

    if (item.id) {
      this.selectedKeys = [item.id];
    } else {
      this.selectedKeys = [];
    }
  }

  //-----------------------  Tree Lookup Methods  ----------------------

  /** Returns the index of the item within the tree, or -1/-2 when absent. */
  protected findIndexInTree(tree: TreeDataItem[] | undefined | null, itemId: string): number {

    if (tree) {

      for (let i = 0; i < tree.length; i++) {

        if (tree[i].id === itemId) {
          return i; // Item found at the current level
        }

        if (tree[i].items) {

          const indexInChildren = this.findIndexInTree(tree[i].items, itemId);

          if (indexInChildren !== -1) {
            return indexInChildren; // Item found in children
          }
        }
      }

      return -1; // Item not found in the current level or its children

    }

    return -2;
  }

  /** Returns the parent of the item within the tree, if any. */
  protected findParent(data: TreeDataItem[], itemId: string): TreeDataItem | undefined {

    for (const item of data) {

      if (item.items
        && item.items.some(child => child.id === itemId)) {
        return item;
      }

      if (item.items) {

        const found = this.findParent(item.items, itemId);

        if (found) {
          return found;
        }
      }
    }

    return undefined;
  }

  /** Returns the item within the tree matching the id, if any. */
  protected findItemInTree(data: TreeDataItem[], itemId: string): TreeDataItem | undefined {

    for (const item of data) {

      if (item.id === itemId) {
        return item;
      }

      if (item.items) {

        const found = this.findItemInTree(item.items, itemId);

        if (found) {
          return found;
        }
      }
    }

    return undefined;
  }

  //--------------------  Tree Check State Methods  --------------------

  /** Applies the supplied ids as the checked items. */
  protected applyCheckedIds(ids: string[]): void {

    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }

  /** Applies the supplied ids as the highlighted items. */
  protected applyHighlightedIds(ids: string[]): void {

    this.dataItems.forEach((item) => {
      item.highlighted = ids.includes(item.id!);
    });
  }

  // return 1 for all children checked, 0 for none , -1 for some
  protected hasCheckedChildren(items: TreeDataItem[] | undefined | null): number {

    if (!Array.isArray(items)
      || !items.length) {
      return 0;
    }

    let countChecked: number = 0;
    let countIndeterminate: number = 0;

    for (let idx = 0; idx < items.length; idx++) {

      let item = items[idx];

      if ((!Array.isArray(item.items)
        || !item.items.length)) {

        // has no children
        if (this.containsCheckedItem(item))
          countChecked++;

      }
      else {

        let childState = this.hasCheckedChildren(item.items);

        if (childState === 1) {
          countChecked++;
        }
        else if (childState === -1) {
          countIndeterminate++;
        }
      }
    }

    if (countChecked > 0) {

      if (countChecked < items.length)
        return -1;
      else if (countChecked == items.length)
        return 1;

    }
    else if (countIndeterminate > 0)
      return -1;

    return 0;
  }

  /** True when the item's key is in the checked keys. */
  protected containsCheckedItem(item: any): boolean {

    return this.checkedKeys.indexOf(item[this.key]) > -1;
  }

  //-------------------------  Styling Methods  ------------------------

  /** Appends data-type css classes to the items recursively. */
  protected setCssClass(items: TreeDataItem[] | undefined | null) {

    if (!Array.isArray(items)
      || !items.length) {
      return;
    }

    items.forEach(x => {

      if (x.dataType
        && x.dataType.length > 0) {
        x.spriteCssClass += " isg-" + x.dataType;
      }

      this.setCssClass(x.items);
    });
  }

  /** Resolves the css classes for an item from its lock/exclude/custom state. */
  protected getCssClass(item: TreeDataItem) {

    let ci: string = "";

    if (item.isLocked) {
      ci = " isg-locked";
    }

    if (this.excludedIds.includes(item.id!, 0)) {
      ci += " isg-exclude";
    }
    else if (this.customIds.includes(item.id!, 0)) {
      ci += " isg-user-custom";
    }

    if (this.hasChildExcludedIds.includes(item.id!, 0)) {
      ci += " isg-exclude-child";
    }

    if (this.hasChildCustomIds.includes(item.id!, 0)) {
      ci += " isg-user-custom-child";
    }

    ci = item.spriteCssClass + ci;

    return ci;
  }

  //-------------------  Data Transformation Methods  ------------------

  /** Prepares the editor item for add or edit mode. */
  protected handlePermissionDetails(isAddMode: boolean, permissionDetail: AuthPerm) {

    if (isAddMode) {
      this.editDataItem = new AuthPerm();
      this.editDataItem.parentId = permissionDetail?.id;
      this.editDataItem.hierarchyId = permissionDetail.hierarchyId;
    } else {
      this.editDataItem = permissionDetail;
    }
  }

  /** Collects checked permission assignments from the tree recursively. */
  protected getPermissionAssignments(items: TreeDataItem[]
    , checkedKeys: string[]
    , result: Map<string
      , AuthPermIdentifier>): boolean {

    let added: boolean = false;

    for (const item of items) {

      if (item.id
        && checkedKeys.includes(item.id)) {
        result.set(item.id, { id: item.id, code: item.dataCode });
        added = true;
      }

      if (item.items
        && item.items.length > 0) {

        let childAdded = this.getPermissionAssignments(item.items, checkedKeys, result);

        if (childAdded == true) {
          result.set(item.id!, { id: item.id, code: item.dataCode });
          added = true;
        }
      }
    }

    return added;
  }

  /** Clears the selected and editor items. */
  protected resetSelectedItems(): void {

    this.selectedItem = null;
    this.editDataItem = null;
  }

  //---------------------------  Api Methods  --------------------------

  /** Fetches the permission trees for the current filters. */
  protected getPermissionTrees(): void {

    this.loading = true;
    this.checkedKeys.length = 0;

    const req: RequestPermissionTree = {
      licenseId: this.licenseId()
      , moduleIds: this.moduleIds()
      , hierarchyId: this.hierarchyId()
      , authTypes: this.authTypes()
      , authTypesExclude: this.authTypesExclude()
      , includeMembers: this.includeMembers()
      , includeHierarchies: this.includeHierarchies()
      , includeLabels: this.includeLabels()
      , cascadeLink: this.cascadeLink()
      , active: this.active()
      , processFilter: this.processFilter()
    };

    this.authService.permissionTreeGet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe(
        resp => {

          this.setCssClass(resp.data);

          this.dataItems = resp.data!;
          this.dataLoaded.emit(this.dataItems?.length);

          if (resp.data
            && resp.data.length > 0) {
            this.getAllParentTextProperties(resp.data);
          }
        }
      );
  }

  /** Fetches the checked permissions for the current role. */
  protected getRolePermission() {

    this.loading = true;
    this.checkedKeys.length = 0;

    const req: RequestRolePerm = {
      moduleId: this.moduleIds()?.join()
      , licenseId: this.licenseId()
      , roleId: this.roleId()
    }

    this.authService.rolePermissionGet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.data) {

          this.checkedKeysCopy = res.data.reduce((keys: string[], item) => {

            if (!!item.id) {
              keys.push(item.id);
            }

            return keys;
          }, []);

          this.checkedKeys = [...this.checkedKeysCopy];
        }
      });
  }

  /** Fetches the detail for the selected permission and opens the editor. */
  protected getPermissionDetail(isAddMode: boolean = false) {

    if (this.selectedItem?.id) {

      const req: RequestPermission = {
        id: this.selectedItem.id
        , moduleId: this.moduleIds()?.join()
      };

      this.authService.permissionGet$(req)
        .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
        .subscribe(
          resp => {

            if (resp.data
              && resp.data.length > 0) {

              const permissionDetail = resp.data[0];

              this.handlePermissionDetails(isAddMode, permissionDetail);
            }
          }
        );
    }
  }

  /** Creates a permission and refreshes the tree on success. */
  protected createPermission(request: RequestPermissionSet): void {

    this.authService.permissionSet$(request).subscribe(
      (response) => {

        if (response.success) {
          this.notification.showNotification('permission added succesfully', 'success');
          this.refreshPermissions();
        }
      }
    );
  }

  /** Updates a permission and refreshes the tree on success. */
  protected updatePermission(request: RequestPermissionSet): void {

    this.authService.permissionUpdate$(request).subscribe(
      (response) => {

        if (response.success) {
          this.notification.showNotification('permission updated succesfully', 'success');
          this.refreshPermissions();
        }
      }
    );
  }

  /** Deletes a permission and refreshes the tree on success. */
  protected deletePermission(permissionId: string): void {

    const req: RequestDelete = {
      id: permissionId
      , moduleId: this.moduleIds()?.join()
    };

    this.authService.permissionDelete$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.success) {
          this.notification.showNotification('permission deleted succesfully', 'success');
          this.refreshPermissions();
        }
      });
  }

  /** Persists the checked permissions for the current role. */
  protected saveRolePermissions() {

    // reset the list
    this.checkedDataItems.length = 0;

    let assignments = new Map<string, AuthPermIdentifier>();

    this.getPermissionAssignments(this.dataItems, this.checkedKeys, assignments);

    if (this.roleId()) {

      this.loading = true;

      const req: RequestRolePermSet = {
        licenseId: this.licenseId()
        , moduleId: this.moduleIds()?.join()
        , roleId: this.roleId()
        , hierarchyId: this.hierarchyId()
        , authTypes: this.authTypes()
        , data: [...assignments.values()]
      };

      this.authService.rolePermissionSet$(req)
        .pipe(takeUntil(this.destroy$)
          , finalize(() => {
            this.loading = false;
            this.permissionsSavedChange.emit(false);
            assignments.clear();
          }))
        .subscribe((res) => {

          if (res.success) {
            this.dialogNotification.openNotificationDialog(this.ctTitleSuccess, this.ctMsgPermsSaved);
          }
          else {
            this.dialogNotification.openNotificationDialog(this.ctTitleError, this.ctMsgError);
          }
        });
    }
  }

  /** Persists the checked permissions for the current user. */
  protected saveUserPermissions() {

    // reset the list
    this.checkedDataItems.length = 0;

    let assignments = new Map<string, AuthPermIdentifier>();

    this.getPermissionAssignments(this.dataItems, this.checkedKeys, assignments);

    const userFilterId = this.userFilterId();
    const hierarchyId = this.hierarchyId();

    if (userFilterId) {

      this.loading = true;

      const req: RequestUserPermissionSet = {
        userIds: userFilterId ? [userFilterId] : null
        , licenseId: this.licenseId()
        , moduleId: this.moduleIds()?.join()
        , updateRoles: false
        , updatePerms: true
        , roleIds: null
        , hierarchyIds: hierarchyId ? [hierarchyId] : null
        , authTypes: this.authTypes()
        , data: [...assignments.values()]
      };

      this.authService.userPermissionSet$(req)
        .pipe(takeUntil(this.destroy$)
          , finalize(() => {
            this.loading = false;
            assignments.clear();
            this.permissionsSavedChange.emit(false);
          }))
        .subscribe((res) => {

          if (res.success) {
            this.dialogNotification.openNotificationDialog(this.ctTitleSuccess, this.ctMsgPermsSaved);
          }
          else {
            this.dialogNotification.openNotificationDialog(this.ctTitleError, this.ctMsgError);
          }
        });
    }
  }

  /** Refreshes the permission tree. */
  protected refreshPermissions(): void {

    this.getPermissionTrees();
  }

}
